import Bugsnag from '@bugsnag/js'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { Row, Space } from 'antd'
import { MemberProps } from 'app/components/Core/convention'
import KCheckbox from 'app/components/Modules/Form/KCheckbox'
import KForm from 'app/components/Modules/Form/KForm'
import KInputText from 'app/components/Modules/Form/KInputText'
import Icon from 'app/components/UI-Elements/General/Icon'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import { RecordType } from 'app/constants/GlobalTypes'
import { useFeedbacker } from 'app/lib/contexts/Feedbacker'
import { ToolBox } from 'app/lib/hooks/toolBox'
import { useCreate } from 'app/lib/hooks/useApi'
import useTranslation from 'app/lib/hooks/useTranslation'
import * as React from 'react'
import { useState } from 'react'

import CardElements from './Elements'

const options = ['master_card', 'american_express', 'visa']

const CreditCard: React.FC<
  MemberProps<ICreditCard, Partial<React.ComponentProps<typeof KForm>>>
> = ({ member, props: { onSuccess, ...others } = {} }) => {
  const { tAttribute } = ToolBox.useString()
  const { tCore } = useTranslation({ scope: 'form', recordType: RecordType.credit_card })
  const { execute } = useCreate(RecordType.payment_intent, null, true)
  const [loading, setLoading] = useState(false)
  const { toastify } = useFeedbacker()
  const stripe = useStripe()
  const elements = useElements()

  const onError = (error?: string) => {
    setLoading(false)
    if (error) {
      toastify({ content: error, type: 'error' })
    }
  }

  const onFinish = async (data: { card_holder: string }) => {
    setLoading(true)
    try {
      const response: { client_secret?: string } = await execute({
        quote_id: member.quote_id
      })

      if (!response?.client_secret) {
        onError()
      } else {
        const { error } = await stripe.confirmCardPayment(response.client_secret, {
          payment_method: {
            card: elements.getElement('cardNumber'),
            billing_details: {
              name: data.card_holder
            }
          }
        })

        if (error) {
          Bugsnag.notify(JSON.stringify(error), (event) => {
            event.severity = 'info'
          })
          onError(error.message)
        } else {
          setLoading(false)
          onSuccess(data)
        }
      }
    } catch (err) {
      Bugsnag.notify(err, (event) => {
        event.severity = 'info'
      })
      onError()
    }
  }

  if (!stripe || !elements) {
    return <p>{tCore({ id: 'stripe_failed' })}</p>
  }

  return (
    <KForm
      initialFetch={false}
      silentSuccess={true}
      initialValues={member}
      recordType={RecordType.credit_card}
      layout="vertical"
      onFinish={onFinish}
      loading={loading}
      {...others}>
      <Row gutter={[0, 24]}>
        <KCol span={24}>
          <Space direction="horizontal">
            {options.map((card) => (
              <Icon key={card} type={card} iconStyles={{ maxWidth: 45 }} />
            ))}
          </Space>
        </KCol>
        <KCol span={24}>
          <Row>
            <KCol span={24}>
              <KInputText required hasPlaceholder name="card_holder" />
            </KCol>
            <KCol span={24} loading={!stripe} id="card-element">
              <If condition={stripe}>
                <CardElements />
              </If>
            </KCol>
          </Row>
        </KCol>
        <KCol span={24}>
          {tAttribute({
            attribute: 'cvc',
            recordType: RecordType.credit_card,
            values: {
              msg: (val: string) => <strong>{val}</strong>
            }
          })}
        </KCol>
        <KCol span={24}>
          <KCheckbox required hasFeedback={false} name="agb" />
        </KCol>
      </Row>
    </KForm>
  )
}

export default CreditCard
